import { MenuDivider, MenuItem, MenuItemProps } from "@chakra-ui/react";
import React, { useState } from "react";
import { FiEdit2, FiGlobe } from "react-icons/fi";
import {
  HiOutlineLockClosed,
  HiOutlineLockOpen,
  HiOutlineTrash,
} from "react-icons/hi";
import { MdOutlineFileDownload } from "react-icons/md";

import {
  canDownloadRecording,
  canManageTraining,
} from "../../../utils/permissions";
import { Call, useCallRecordingDownloadUrlLazyQuery } from "../../graphql";
import useCurrentUser from "../../hooks/useCurrentUser";
import AddToTrainingProgramIcon from "../AddToTrainingProgramModal/AddToTrainingProgramIcon";
import AddToTrainingProgramModal from "../AddToTrainingProgramModal/AddToTrainingProgramModal";
import DeleteCallButton from "../CallSettingsMenu/DeleteCallButton";
import KeepCallButton from "../CallSettingsMenu/KeepCallButton";
import UnkeepCallButton from "../CallSettingsMenu/UnkeepCallButton";

export const menuItemProps = (
  { dangerous } = { dangerous: false }
): MenuItemProps => ({
  fontSize: "sm",
  iconSpacing: "4",
  px: "4",
  py: "2.5",
  color: dangerous ? "red.600" : "blue.600",
  _hover: dangerous
    ? { bg: "red.50", color: "red.500" }
    : { bg: "blue.50", color: "blue.500" },
});

const useCallMenuOptions = (
  call: Pick<
    Call,
    "id" | "type" | "canEdit" | "skipRedaction" | "canDelete" | "name"
  >,
  { onEdit, onSetLanguage }: { onEdit?(): void; onSetLanguage?(): void } = {}
): JSX.Element[] => {
  const currentUser = useCurrentUser();
  const [addToTraining, setAddToTraining] = useState(false);

  const [getDownloadUrl] = useCallRecordingDownloadUrlLazyQuery();
  const download: () => void = () => {
    getDownloadUrl({
      variables: {
        callId: call.id,
      },
      onCompleted(data) {
        const url = data?.callRecordingDownloadUrl;
        if (url) {
          const downloadLink = document.createElement("a");
          downloadLink.style.display = "none";
          downloadLink.href = url;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }
      },
    });
  };

  const menuOptions: JSX.Element[] = [];
  const isAiInterview = call.type === "AI_INTERVIEW";

  if (!isAiInterview) {
    // Add to Training
    if (canManageTraining(currentUser)) {
      menuOptions.push(
        <MenuItem
          {...menuItemProps()}
          key="add-to-training"
          icon={<AddToTrainingProgramIcon boxSize="5" />}
          onClick={() => setAddToTraining(true)}
        >
          Add to Training Program
          {addToTraining && (
            <AddToTrainingProgramModal
              callId={call.id}
              onClose={() => setAddToTraining(false)}
            />
          )}
        </MenuItem>
      );
    }

    if (call.canEdit) {
      // Edit details
      menuOptions.push(
        <MenuItem
          {...menuItemProps()}
          key="edit-position-button"
          icon={<FiEdit2 size="18" />}
          data-testid="edit-position-button"
          onClick={onEdit}
        >
          Update Interview Details
        </MenuItem>
      );

      // Set language
      menuOptions.push(
        <MenuItem
          {...menuItemProps()}
          key="set-language"
          icon={<FiGlobe size="18" />}
          data-testid="set-language-button"
          onClick={onSetLanguage}
        >
          Change transcript language
        </MenuItem>
      );
    }
  }

  if (call.canDelete) {
    // Keep / unkeep call
    if (call.skipRedaction) {
      menuOptions.push(
        <UnkeepCallButton
          key="unkeep-call-button"
          call={call}
          icon={<HiOutlineLockOpen size="20" />}
          {...menuItemProps({ dangerous: true })}
        />
      );
    } else {
      menuOptions.push(
        <KeepCallButton
          key="keep-call-button"
          call={call}
          icon={<HiOutlineLockClosed size="20" />}
          {...menuItemProps()}
        />
      );
    }
  }

  if (canDownloadRecording(currentUser)) {
    // Download recording
    menuOptions.push(
      <MenuItem
        {...menuItemProps()}
        key="download-recording"
        icon={<MdOutlineFileDownload size="20" />}
        onClick={download}
      >
        Download Recording
      </MenuItem>
    );
  }

  if (menuOptions.length > 0) {
    menuOptions.push(<MenuDivider />);
  }

  // Delete -- always shown, but disabled if !call.canDelete
  menuOptions.push(
    <DeleteCallButton
      key="delete-call-button"
      call={call}
      icon={<HiOutlineTrash size="20" />}
      {...menuItemProps({ dangerous: true })}
    />
  );

  return menuOptions;
};

export default useCallMenuOptions;
